import { combineSlices, configureStore } from '@reduxjs/toolkit';
import authSlice from './slices/auth.slice';
import { dataGridApi } from './api/datagrid.api';
import layoutSlice, { LAYOUT_SLICE_ID } from './slices/layout.slice';
import pageComposerSlice, { COMPOSER_SLICE_ID } from './slices/page-composer.slice';
import { chartApi } from './api/chart.api';
import { featureApi } from './api/feature.api';
import filterBuilderSlice from './slices/filter-builder.slice';
import dateFilterSlice, { DATE_SLICE_ID } from './slices/date-filter.slice';
import { filterApi } from './api/filters.api';
import commonFilterSlice, { COMMON_FILTER_SLICE_ID } from './slices/common-filters.slice';
import datasetSlice from './slices/dataset.slice';
import { questionApi } from './api/question.api';
import searchSlice from './slices/search.slice';
import viewToggleSlice from './slices/view-toggle.slice';
import tagEditorSlice from './slices/tag-editor.slice';
import { tagsApi } from './api/tags.api';
import chartFilterSlice, { CHART_FILTER_SLICE_ID } from './slices/chart-filters.slice';
import selectionSlice from './slices/selection.slice';
import requestFilterSlice from './slices/request-filter.slice';
import exportSlice from './slices/export.slice';
import notificationSlice from './slices/notification.slice';
import dashboardEditorSlice from './slices/dashboard-editor.slice';
import exportPptModalSlice from './slices/export-ppt.slice';
import { reportsApi } from './api/report.api';
import confirmSlice from './slices/confirm.slice';
import { filesApi } from './api/files.api';
import flyoutSlice from './slices/flyout.slice';
import { quickviewsApi } from './api/quickviews.api';
import coachSlice from './slices/coach.slice';
import elsieAiSlice from './slices/elsie-ai.slice';
import insightsCaptureSlice from './slices/insights-capture.slice';
import { elsieApi } from './api/elsie.api';
import { insightsCaptureApi } from './api/insightsCapture.api';
import savedFiltersSlice, { SAVED_FILTERS_SLICE_ID } from './slices/saved-filters.slice';
import { userDataApi } from './api/user-data.api';
import { teamsApi } from './api/teams.api';
import saveCriteriaModalsSlice from './slices/save-criteria-modals.slice';

import filterParentSlice from './slices/filter-parent.slice';
import searchFilterSlice from './slices/search-query.slice';
import { authApi } from './api/auth.api';
import pageConfigSlice, { CONFIG_SLICE_ID } from './slices/page-config.slice';
import paginationSlice from './slices/pagination.slice';
import excelExportSlice from './slices/excel.slice';
import widgetFilterSlice from './slices/widget-filter.slice';
import widgetSortSlice from './slices/widget-sort.slice';
import addToReportSlice from './slices/add-to-report-modal.slice';
import widgetConfigSlice from './slices/widget-config.slice';
import gridConfigSlice from './slices/grid-config.slice';

import { persistStore, persistReducer } from 'redux-persist';
import { createLogger } from 'redux-logger';
import storageSession from 'redux-persist/lib/storage/session';
import allWidgetDataSlice from './slices/widget-data.slice';
import aggregatesSlice from './slices/responseData/aggregatesSlice';
import { planningApi } from './api/planning.api';
import modalSlice, { MODAL_SLICE_ID } from './slices/modal.slice';
import asyncExcelExportSlice from './slices/async-excel-export.slice';
import { powerBiApi } from './api/powerBi.api';
import { moduleProjectApi } from './api/moduleProject.api';

export const rootReducer = combineSlices(
  addToReportSlice,
  aggregatesSlice,
  allWidgetDataSlice,
  asyncExcelExportSlice,
  authApi,
  authSlice,
  chartApi,
  chartFilterSlice,
  coachSlice,
  commonFilterSlice,
  confirmSlice,
  dashboardEditorSlice,
  dataGridApi,
  datasetSlice,
  dateFilterSlice,
  elsieAiSlice,
  elsieApi,
  excelExportSlice,
  exportPptModalSlice,
  exportSlice,
  featureApi,
  filesApi,
  filterApi,
  filterBuilderSlice,
  filterParentSlice,
  flyoutSlice,
  gridConfigSlice,
  insightsCaptureApi,
  insightsCaptureSlice,
  layoutSlice,
  modalSlice,
  moduleProjectApi,
  notificationSlice,
  pageComposerSlice,
  pageConfigSlice,
  paginationSlice,
  planningApi,
  powerBiApi,
  questionApi,
  quickviewsApi,
  reportsApi,
  requestFilterSlice,
  saveCriteriaModalsSlice,
  savedFiltersSlice,
  searchFilterSlice,
  searchSlice,
  selectionSlice,
  tagEditorSlice,
  tagsApi,
  teamsApi,
  userDataApi,
  viewToggleSlice,
  widgetConfigSlice,
  widgetFilterSlice,
  widgetSortSlice
).withLazyLoadedSlices();

const persistConfig = {
  key: 'root',
  storage: storageSession,
  whitelist: [
    LAYOUT_SLICE_ID,
    COMMON_FILTER_SLICE_ID,
    DATE_SLICE_ID,
    SAVED_FILTERS_SLICE_ID,
    CHART_FILTER_SLICE_ID,
    CONFIG_SLICE_ID,
    MODAL_SLICE_ID,
    COMPOSER_SLICE_ID,
  ],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Disabling un-used vars so that it's easier to enable logger for debugging
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = createLogger({
  duration: true,
  timestamp: false,
  predicate: (_getState, action) => {
    const hideLogs: string[] = ['middleware', 'persist/', 'Api/'];
    const showOnly: string[] = [];

    if (hideLogs.some((log) => action.type.includes(log))) {
      return false;
    }

    if (showOnly.length > 0) {
      return showOnly.some((log) => action.type.includes(log));
    }

    return true;
  },
});

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat([
      authApi.middleware,
      chartApi.middleware,
      dataGridApi.middleware,
      elsieApi.middleware,
      featureApi.middleware,
      filesApi.middleware,
      filterApi.middleware,
      insightsCaptureApi.middleware,
      moduleProjectApi.middleware,
      planningApi.middleware,
      powerBiApi.middleware,
      questionApi.middleware,
      quickviewsApi.middleware,
      reportsApi.middleware,
      tagsApi.middleware,
      teamsApi.middleware,
      userDataApi.middleware,
      // logger,
    ]),
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof rootReducer>;

export default store;
